import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * Minimal UI-state context for the side navigation panel.
 * Replaces the old Redux homepage-slice flags (showSidePanel,
 * sidePanelExpanded, selectedSidePanelView).
 *
 * NOTE: The side panel is slated for removal — keep this as thin as
 * possible. When the panel goes, delete this file and its 3 consumers
 * (SideNavigationMenu, BuilderTopBar, FeatureContext).
 */

const SidePanelContext = createContext(null);

export function SidePanelProvider({ children }) {
  const [showSidePanel, setShowSidePanel] = useState(true);
  const [sidePanelExpanded, setSidePanelExpanded] = useState(false);
  const [selectedSidePanelView, setSelectedSidePanelView] = useState(null);

  const value = useMemo(
    () => ({
      showSidePanel,
      setShowSidePanel,
      sidePanelExpanded,
      setSidePanelExpanded,
      selectedSidePanelView,
      setSelectedSidePanelView
    }),
    [showSidePanel, sidePanelExpanded, selectedSidePanelView]
  );

  return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
}

// Null-safe: returns no-op setters if the provider is missing (e.g. isolated
// component tests), so consumers never crash.
const noop = () => {};
const fallback = {
  showSidePanel: true,
  setShowSidePanel: noop,
  sidePanelExpanded: false,
  setSidePanelExpanded: noop,
  selectedSidePanelView: null,
  setSelectedSidePanelView: noop
};

export function useSidePanel() {
  return useContext(SidePanelContext) ?? fallback;
}
